import { useEffect } from 'react'
import { useLocation } from '@tanstack/react-router'

/**
 * Where the user was before they opened a settings screen.
 *
 * "Close settings" returns to that place, so opening the settings and
 * closing them again does not lose the board, the workspace or the page the
 * user was on. The settings tabs are routes, so the browser history holds
 * every tab switch — the place the user came from is tracked separately.
 */

/** The last location that was not a settings screen. */
let origin: string | null = null

/** Remembers the last location that was not a settings screen. */
export function rememberSettingsOrigin(pathname: string | null): void {
  if (!pathname) return
  if (isSettingsPath(pathname)) return
  origin = pathname
}

/** The remembered origin, or null when the user opened the settings directly. */
export function getSettingsOrigin(): string | null {
  return origin
}

/** Forgets the origin (used when a workspace switch invalidates it). */
export function clearSettingsOrigin(): void {
  origin = null
}

/** Whether a path points at one of the settings screens. */
function isSettingsPath(pathname: string): boolean {
  return /\/settings(\/|$)/.test(pathname)
}

/**
 * Tracks the last location outside the settings screens. The app shell calls
 * it on every navigation.
 */
export function useTrackSettingsOrigin(): void {
  const location = useLocation()
  useEffect(() => {
    rememberSettingsOrigin(location.pathname)
  }, [location.pathname])
}
